package com.neurobs.ccab.casr

import com.neurobs.errors._
import com.neurobs.util.{TimeSeconds, TimeDMS}
import com.neurobs.util.TimeSeconds._
import io.circe._
import io.circe.generic.auto._
import io.circe.generic.semiauto._
import io.circe.syntax.EncoderOps

object CasrDatabaseJSON {

  abstract class DatabaseJSON[T <: RecordingJSON](recordings: Seq[T]) {
    def recordings(): Seq[T] = recordings

    def recordingTranscriptFromCode(code: String): Option[T] = {
      recordings.find(_.code() == code)
    }
  }

  private object DatabaseJSON {
    def fromJsonString[V <: RecordingJSON, U <: DatabaseJSON[V]](jsonString: String, stringDecoder: String => Either[io.circe.Error, Seq[V]], classConstructor: Seq[V] => U): Result[U] = {
      lazy val recordings = stringDecoder(jsonString)
      recordings match {
        case Left(error) => Left(error)
        case Right(rs) => Right(classConstructor(rs))
      }
    }
  }

  case class TestTranscriptJSON(testTranscriptRecordings: Seq[TestTranscriptRecordingJSON])
    extends DatabaseJSON[TestTranscriptRecordingJSON](testTranscriptRecordings)

  object TestTranscriptJSON  {
    def fromJsonString(jsonString: String): Result[TestTranscriptJSON] = {
      def stringDecoder: String => Either[Error, Seq[TestTranscriptRecordingJSON]] = (string: String) => parser.decode[Seq[TestTranscriptRecordingJSON]](string)
      def classConstructor: Seq[TestTranscriptRecordingJSON] => TestTranscriptJSON = (recordings: Seq[TestTranscriptRecordingJSON]) => TestTranscriptJSON(recordings)
      DatabaseJSON.fromJsonString(jsonString, stringDecoder, classConstructor)
    }
  }

  case class AltTestTranscriptJSON(testTranscriptRecordings: Seq[AltTestTranscriptRecordingJSON], softwareVersion: Int, configVersion: Int)
    extends DatabaseJSON[AltTestTranscriptRecordingJSON](testTranscriptRecordings)

  object AltTestTranscriptJSON {
    def fromTestTranscriptJSON(testTranscriptJSON: TestTranscriptJSON, softwareVersion: Int, configVersion: Int): AltTestTranscriptJSON = {
      val altTestTranscriptRecordings = testTranscriptJSON.testTranscriptRecordings.map(r => AltTestTranscriptRecordingJSON.fromTestTranscriptRecording(r, softwareVersion, configVersion))
      AltTestTranscriptJSON(altTestTranscriptRecordings, softwareVersion, configVersion)
    }
  }

  case class ReviewedTestTranscriptJSON(reviewedTestTranscriptRecordings: Seq[ReviewedTestTranscriptRecordingJSON])
    extends DatabaseJSON[ReviewedTestTranscriptRecordingJSON](reviewedTestTranscriptRecordings) {

    def toJsonString(): String = {
      this.reviewedTestTranscriptRecordings.asJson.toString.filterNot(_.isWhitespace)
    }
  }


  object ReviewedTestTranscriptJSON {
    def fromJsonString(jsonString: String): Result[ReviewedTestTranscriptJSON] = {
      def stringDecoder: String => Either[Error, Seq[ReviewedTestTranscriptRecordingJSON]] = (string: String) => parser.decode[Seq[ReviewedTestTranscriptRecordingJSON]](string)
      def classConstructor: Seq[ReviewedTestTranscriptRecordingJSON] => ReviewedTestTranscriptJSON = (recordings: Seq[ReviewedTestTranscriptRecordingJSON]) => ReviewedTestTranscriptJSON(recordings)
      DatabaseJSON.fromJsonString(jsonString, stringDecoder, classConstructor)
    }
  }

  abstract class RecordingJSON(c: String, recordingOffset: TimeSeconds) {
    // Remove the leading 0's put into the converted wavefiles for some processed audio files
    // by the backend
    val converted_code =  c.replaceAll("""0*([1-9]\d*|0)""", "$1")
    def code(): String = converted_code
    def recordingOffset(): TimeSeconds = recordingOffset
  }


  case class ReviewedTestTranscriptRecordingJSON(c: String, ro: TimeSeconds, t: ReviewedTranscriptJSON) extends RecordingJSON(c, ro) {
    def reviewedTranscript(): ReviewedTranscriptJSON = t
  }

  case class TestTranscriptRecordingJSON(c: String, ro: TimeDMS, ts: Seq[EngineTranscriptJSON]) extends RecordingJSON(c, ro.timeSeconds()) {
    def casrTranscript(): EngineTranscriptJSON = {
		 // The new architecture test_transcript records do not include anything if all engines return empty transcripts
		 ts.find(_.engineIdentifier() == EngineIdentifiers.CASR) match {
			 case Some(t) => t
			 case None => EngineTranscriptJSON(eid = EngineIdentifiers.CASR, ws = Seq.empty)
		 }
	 }
    def asrTranscripts(): Seq[EngineTranscriptJSON] = ts.filterNot(_.engineIdentifier() == EngineIdentifiers.CASR)
  }

  case class AltTestTranscriptRecordingJSON(c: String, ro: TimeDMS, ts: Seq[EngineTranscriptJSON], sv: Int, cv: Int) extends RecordingJSON(c, ro.timeSeconds()) {
    def casrTranscript(): EngineTranscriptJSON = {
		 ts.find(_.engineIdentifier() == EngineIdentifiers.CASR) match {
		 	case Some(t) => t
			case None => EngineTranscriptJSON(eid = EngineIdentifiers.CASR, ws = Seq.empty)
		 }
	 }
    def asrTranscripts(): Seq[EngineTranscriptJSON] = ts.filterNot(_.engineIdentifier() == EngineIdentifiers.CASR)
  }

  object AltTestTranscriptRecordingJSON {
    def fromTestTranscriptRecording(testTranscriptRecordingJSON: TestTranscriptRecordingJSON, softwareVersion: Int, configVersion: Int): AltTestTranscriptRecordingJSON = {
      AltTestTranscriptRecordingJSON(testTranscriptRecordingJSON.c, testTranscriptRecordingJSON.ro, testTranscriptRecordingJSON.ts, softwareVersion, configVersion)
    }
  }

  abstract class DatabaseTranscriptJSON(engineIdentifier: String, words: Seq[DatabaseWordJSON]) {
    def engineIdentifier(): String = engineIdentifier
    def words(): Seq[DatabaseWordJSON] = words
  }

  case class EngineTranscriptJSON(eid: String, ws: Seq[EngineWordJSON]) extends DatabaseTranscriptJSON(eid, ws) {
    override def words() = ws
  }

  case class ReviewedTranscriptJSON(eid: String, u: String, l: Boolean,  ws: Seq[ReviewedWordJSON]) extends DatabaseTranscriptJSON(EngineIdentifiers.REVIEWED, ws) {
    def username() = u
    def latency() = l
  }

  abstract class DatabaseWordJSON(word: String, startTime: TimeSeconds, endTime: TimeSeconds) {
    def word(): String = word
    def startTime(): TimeSeconds = startTime
    def endTime(): TimeSeconds = endTime
    def isEmpty: Boolean = word().isEmpty
    def newTimes(startTime: TimeSeconds, endTime: TimeSeconds): DatabaseWordJSON
    def duration(): TimeSeconds = endTime() - startTime()
  }

  object DatabaseWordJSON {

    def createEncoder[T <: DatabaseWordJSON](encoder: Encoder.AsObject[T]): Encoder[T] = new Encoder[T] {
      final def apply(word: T): Json = {
        if (word.isEmpty) Json.Null
        else {
          encoder.encodeObject(word).asJson
        }
      }
    }

    def createDecoder[T <: DatabaseWordJSON](emptyWordGenerator: () => T, decoder: Decoder[T]): Decoder[T] = new Decoder[T] {
      final def apply(cursor: HCursor): Decoder.Result[T] = {
        val result = cursor.as[Json]
        result match {
          case Left(failure) => Left(failure)
          case Right(json) =>
            if (json == Json.Null) {
              Right(emptyWordGenerator())
            } else {
              decoder.decodeJson(json)
            }
        }
      }
    }

  }

  case class EngineWordJSON(w: String, st: TimeSeconds, et: TimeSeconds, c: Double) extends DatabaseWordJSON(w, st, et) {
    def confidence(): Double = c
    def newTimes(startTime: TimeSeconds, endTime: TimeSeconds): EngineWordJSON = EngineWordJSON(w = w, st = startTime, et = endTime, c = c)
  }

  object EngineWordJSON {
    def emptyEngineWord() = EngineWordJSON("", TimeSeconds(0), TimeSeconds(0), 0)
    lazy implicit val encodeEngineWord: Encoder[EngineWordJSON] = DatabaseWordJSON.createEncoder[EngineWordJSON](deriveEncoder[EngineWordJSON])
    lazy implicit val decodeReviewedWord: Decoder[EngineWordJSON] = DatabaseWordJSON.createDecoder[EngineWordJSON](emptyEngineWord, deriveDecoder[EngineWordJSON])
  }

  case class ReviewedWordJSON(w: String, st: TimeSeconds, et: TimeSeconds) extends DatabaseWordJSON(w, st, et) {
    def newTimes(startTime: TimeSeconds, endTime: TimeSeconds): ReviewedWordJSON = ReviewedWordJSON(w = w, st = startTime, et = endTime)
  }

  object ReviewedWordJSON {
    def emptyReviewedWord() = ReviewedWordJSON("", TimeSeconds(0), TimeSeconds(0))
    lazy implicit val encodeReviewedWord: Encoder[ReviewedWordJSON] = DatabaseWordJSON.createEncoder[ReviewedWordJSON](deriveEncoder[ReviewedWordJSON])
    lazy implicit val decodeReviewedWord: Decoder[ReviewedWordJSON] = DatabaseWordJSON.createDecoder[ReviewedWordJSON](emptyReviewedWord, deriveDecoder[ReviewedWordJSON])
  }
}

object EngineIdentifiers {

  val AMAZON = "amazon"
  val CASR = "casr"
  val GOOGLE = "google"
  val MS = "ms"
  val REV = "rev"
  val UWP = "uwp"
  val VOSK = "vosk"
  val WATSON = "watson"
  val REVIEWED = "reviewed"

  private val identifierToDisplayMap: Map[String, String] = Map[String, String](
    AMAZON -> "Amazon",
    CASR -> "CASR",
    GOOGLE -> "Google",
    MS -> "Microsoft",
    REV -> "Rev-ai",
    UWP -> "UWP",
    VOSK -> "Vosk",
    WATSON -> "Watson",
  )

  def getDisplayName(engineIdentifier: String) = identifierToDisplayMap.get(engineIdentifier).getOrElse(s"$engineIdentifier")

  def allIdentifiers: Seq[String] = Seq(AMAZON, CASR, GOOGLE, MS, REV, UWP, VOSK, WATSON)
  def allAsrIdentifiers: Seq[String] = allIdentifiers.filter(_ != CASR)
}